import React, { FC, ReactElement, useCallback } from 'react'
import { LabelProps, TagProps } from '@sport1/types/web'
import Touchable from '@sport1/news-styleguide/Touchable'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FlexingContainer from '@sport1/news-styleguide/FlexingContainer'
import Font from '@sport1/news-styleguide/Font'
import Divider from '@sport1/news-styleguide/Divider'
import DropDownOverlay, { DropDownRenderItemProps } from '@/components/DropDownOverlay'
import { pianoOpenCheckout, pianoUserLogout } from '@/components/ThirdParty/Piano/util'
import { useTracking } from '@/utils/tracking/TrackingProvider'
import { readUser, isPureActive } from '@/components/ThirdParty/Piano/util'
import { sport1CondensedBlackItalic, sport1Regular } from '@/helpers/fonts'

type PianoProfileNavigationProps = {
    isMobile: boolean
    show: boolean
    showOverlay: () => void
    setBlur: React.Dispatch<React.SetStateAction<boolean>>
    setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
    setShow: React.Dispatch<React.SetStateAction<boolean>>
}

const tags = (): Array<TagProps | LabelProps> =>
    [
        {
            title: 'Angemeldet als',
            contextId: 'account',
            contextType: 'TAG',
        },
        isPureActive()
            ? undefined
            : {
                  title: 'Abo abschließen',
                  contextId: 'pianoOpenCheckout',
                  contextType: 'TAG',
              },
        {
            title: 'Einstellungen',
            contextId: 'piano-setting',
            contextType: 'TAG',
        },
        {
            title: 'Abmelden',
            contextId: 'logout',
            contextType: 'TAG',
        },
    ].filter(Boolean) as Array<TagProps | LabelProps>

const PianoProfileNavigation: FC<PianoProfileNavigationProps> = ({
    isMobile,
    setBlur,
    show,
    showOverlay,
    setLoggedIn,
    setShow,
}) => {
    const currentUser = readUser()?.email
    const { trackInteraction } = useTracking()

    const itemNavigationPress = useCallback(
        (contextId: string) => {
            trackInteraction({
                devices: 'all',
                interaction_category: 'navigation',
                interaction_action: 'click',
                interaction_label: contextId,
                platforms: isMobile ? 'mobile' : 'desktop',
            })
            if (contextId === 'piano-setting') {
                showOverlay()
            }
            if (contextId === 'logout') {
                pianoUserLogout({ loginStateCallback: setLoggedIn })
            }
            if (contextId === 'pianoOpenCheckout') {
                pianoOpenCheckout()
            }
            setShow(false)
        },
        [trackInteraction, isMobile, setShow, showOverlay, setLoggedIn]
    )

    const renderItem = useCallback(
        ({ tag }: DropDownRenderItemProps): ReactElement => {
            const { title, contextId } = tag
            const itemText = contextId === 'account' ? currentUser : undefined
            const onPress =
                contextId !== 'account' ? () => itemNavigationPress(contextId) : undefined

            return (
                <Touchable
                    onPress={onPress}
                    testID="piano-navigation-item"
                    key={`piano-navigation-item-${contextId}`}
                >
                    <NonFlexingContainer backgroundColor="pearl">
                        <NonFlexingContainer
                            horizontal
                            centerVertical
                            minHeight={itemText ? 0 : 44}
                            paddingY={itemText ? 'spacing-4' : 'spacing-none'}
                            paddingLeft="spacing-7"
                            overflow="hidden"
                        >
                            {itemText ? (
                                <FlexingContainer>
                                    <Font
                                        fontVariant="Label-L"
                                        fontFamilyVariant="Sport1-Regular"
                                        nextFontFamilyVariant={sport1Regular.style.fontFamily}
                                        numberOfLines={1}
                                        color="keshi"
                                    >
                                        {title}
                                    </Font>
                                    <Font
                                        fontVariant="Body-S"
                                        fontFamilyVariant="Sport1-CondensedBlackItalic"
                                        nextFontFamilyVariant={
                                            sport1CondensedBlackItalic.style.fontFamily
                                        }
                                        numberOfLines={2}
                                        color="onyx"
                                    >
                                        {itemText}
                                    </Font>
                                </FlexingContainer>
                            ) : (
                                <Font
                                    fontVariant="Body-S"
                                    fontFamilyVariant="Sport1-CondensedBlackItalic"
                                    nextFontFamilyVariant={
                                        sport1CondensedBlackItalic.style.fontFamily
                                    }
                                    numberOfLines={2}
                                    color="onyx"
                                >
                                    {title}
                                </Font>
                            )}
                        </NonFlexingContainer>
                    </NonFlexingContainer>
                    {contextId !== 'logout' ? <Divider backgroundColor="#f5f5f5" /> : null}
                </Touchable>
            )
        },
        [currentUser, itemNavigationPress]
    )

    return show ? (
        <DropDownOverlay
            testID="piano-profile-navigation"
            top={isMobile ? '75%' : '130%'}
            tags={tags()}
            maxHeight={340}
            width={210}
            renderItem={renderItem}
            onMouseOver={(): void => setBlur(false)}
            onMouseLeave={(): void => setBlur(true)}
        />
    ) : null
}

export default PianoProfileNavigation
